// @ts-strict-ignore
/**
 * Util functions to be used within rows. The ./utils imports from rows, thus utils defined in the file can't be
 * used in rows (causes a circular dependency).
 */

import partition from 'lodash/partition';
import omit from 'lodash/omit';

import {DEFAULT_FILTER_OPERATOR, PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {ProfileHistoryType, Segmentation} from 'common/types/reports/query';
import {FullPropertyType, PropertyType, ResourceType} from 'common/report/constants';
import {BooleanOperator, DisplayContext, ListQuantifier} from 'common/widgets/property-filter-menu/models/types';
import {
  isFirstTimeEventFilter,
  isLookupPropertyFilter,
  PropertyFilterFactory,
} from 'common/widgets/property-filter-menu/models/utils';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {isCohortSegment, SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {COMPUTED_LIST_PROPERTIES_VALUES} from 'common/query-builder/query-block/properties-menu/constants';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {getTempLookupTableMappings} from 'common/report/stores/lookup-tables';
import {propIneligibleForMappingReason} from 'common/widgets/map-lookup-table-modal/util';
import {segmentationToPropertyItem} from 'common/query-builder/query-block/properties-menu/util';
import type {MapLookupTableModalAttrs, PropertyItemWithValue} from 'common/widgets/map-lookup-table-modal';
import {promisifyModal} from 'common/components/modal/utils';
import {prompt} from 'common/components/confirm';
import {MetricType} from 'common/types/reports/bookmark';
import {LocalizationStore} from 'common/i18n/store';
import {
  Action,
  Entry,
  EntryPart,
  isFilterEntry,
  isFormulaEntry,
  isMetricEntry,
  isSavedMetricEntry,
  isSegmentEntry,
  MetricEntry,
  QueryBucketableEntry,
  QueryEventEntry,
  QueryFilterEntry,
  QuerySegmentEntry,
} from 'iron/common/query-builder/query-entry-section/types';
import {QueryEntrySectionTrackingEvent} from 'iron/common/query-builder/query-entry-section/tracking';
import {ControlSelectEntryPoint} from 'iron/common/query-builder/query-entry-section/constants';

import {EntryRowProps} from '../types';
import {getFormulaEntryName, getUpdatedFormulaEntry} from '../formula-entry/utils';
import {getSavedMetricName} from '../metric-entry/metrics/metric-entry-util';

export {getEventNamesFromEntries} from './get-event-names-from-entries';

/**
 * list property object requires additional attributes for the UI.
 *
 * @param listProperty - The property to populate attributes of
 * @param forceList - Force treating the property as a list
 */
export function populateListPropertyAttrs<T extends Partial<Segmentation>>(listProperty: T, forceList?: boolean): T {
  // list quantifier UI for list properties
  const isList =
    forceList || listProperty.propertyType === PropertyType.List || listProperty.joinPropertyType === PropertyType.List;
  if (isList && listProperty.resourceType !== ResourceType.Cohort) {
    if (!isLookupPropertyFilter(listProperty)) {
      listProperty.joinPropertyType = PropertyType.List;
    }

    // if the subtype isn't already set, default it to string.
    const setDefaultSubType = listProperty.propertyType === PropertyType.List;
    if (setDefaultSubType) {
      listProperty.propertyType = PropertyType.String;
      listProperty.propertyDefaultType = PropertyType.String;
    }

    if (listProperty instanceof PropertyFilter) {
      listProperty.listQuantifier = ListQuantifier.Any;
      listProperty.filterOperator = DEFAULT_FILTER_OPERATOR[listProperty.propertyType];

      const isComplexListFilter = listProperty.propertyType === PropertyType.Object;
      if (isComplexListFilter) {
        listProperty.listItemFilters = [];
        listProperty.filterOperator = BooleanOperator.True;
        listProperty.filterValue = true;
      }
    }
  }

  return listProperty;
}

export function populateProfileHistoryOptions<T extends Partial<Segmentation>>(property: T, hideEvents?: boolean) {
  if (property.resourceType === ResourceType.User) {
    return {
      ...property,
      profileHistoryOptions: hideEvents ? {type: ProfileHistoryType.Latest} : {type: ProfileHistoryType.EventTime},
    };
  }

  return property;
}

/**
 * Gets the full datatype of a segmentation.
 */
export function getPropertyType(segmentation: Partial<Segmentation>): FullPropertyType {
  return {
    type: segmentation.propertyType || segmentation.joinPropertyType,
    isList: segmentation.joinPropertyType === PropertyType.List,
  };
}

/**
 * Immutably typecasts a segmentation.
 */
export function setPropertyType<T extends Partial<Segmentation>>(
  segmentation: T,
  newType: FullPropertyType,
  forFilter?: boolean,
): T {
  let newSegmentation: Partial<Segmentation> = {
    ...segmentation,
    propertyType: newType.type,
    // These will be set by the populateListPropertyAttrs call if needed. Defaulting to null for non-lists
    listQuantifier: null,
    joinPropertyType: segmentation.joinPropertyType === PropertyType.Object ? PropertyType.Object : null,
    ...(forFilter
      ? {
          listItemFilters: null,
          filterOperator: null,
          filterValue: null,
        }
      : {
          listItemGroup: null,
          customBucket: null,
          unit: undefined,
          filterOperator: undefined,
          filterValue: undefined,
        }),
  };

  if (forFilter) {
    // turn into PropertyFilter
    newSegmentation = PropertyFilterFactory.createPropertyFilter(newSegmentation, {
      displayContext: DisplayContext.Default,
    });
  } else {
    // default date typecasting to a day breakdown
    if (newType.type === PropertyType.Datetime) {
      newSegmentation.unit = TimeUnit.Day;
    }
  }

  return populateListPropertyAttrs(newSegmentation as T, newType.isList);
}

/**
 * Gets the property type of an underlying entry value.
 */
export function getEntryPropertyType(entry: Entry) {
  if (isMetricEntry(entry)) return null;

  if (isFilterEntry(entry) || isSegmentEntry(entry)) {
    if (Array.isArray(entry.event)) {
      return {
        type: PropertyType.String, // default subtype for lists when we don't know otherwise
        isList: true,
      };
    }

    return getPropertyType(entry.event);
  }

  return null;
}

export function getSegmentationProperty(entry: QuerySegmentEntry): Segmentation | null {
  let segment = entry.event;

  // can be an array when customizing segments
  if (Array.isArray(segment) && segment.length && !isCohortSegment(segment)) {
    segment = entry.event[0] as SegmentValue;
  }

  if (!segment || isCohortSegment(segment) || Array.isArray(segment) || segment.resourceType === ResourceType.Other) {
    return null;
  }

  return segment;
}

export function usingNewBucketing(entry: Entry) {
  return isSegmentEntry(entry) && entry.event;
}

export function isComputedListPropertyEntry(entry: Entry) {
  // TODO(CC): evaluate isComputedListPropertyEntry for metric entry
  if (isMetricEntry(entry)) {
    return false;
  }
  const property = `propertyName` in entry.event && entry.event.propertyName;

  return typeof property === `string` && COMPUTED_LIST_PROPERTIES_VALUES.has(property);
}

/**
 * Checks if an entry contains a first time event filter in any position.
 */
export function hasFirstTimeEventFilter(entry: Entry) {
  const filters = isMetricEntry(entry) || isSavedMetricEntry(entry) ? [] : entry.filters;
  return filters.some(isFirstTimeEventFilter);
}

export function getEntryFilters(
  entry: Entry,
  behaviorMode: boolean,
): {filters: PropertyFilter[]; behavioralFrequencyFilters?: PropertyFilter[]} {
  let filters: PropertyFilter[] = [];
  if (isMetricEntry(entry)) {
    filters = entry.behavior.filters || [];
  } else {
    filters = entry.filters || [];
  }
  if (!behaviorMode) {
    return {
      [EntryPart.Filters]: filters,
    };
  }

  const [behavioralFrequencyFilters, finalFilters] = partition(
    filters,
    (filter) => filter.customProperty && isBehavioralCustomProperty(filter.customProperty),
  );

  return {
    [EntryPart.Filters]: finalFilters,
    [EntryPart.BehavioralFrequencyFilters]: behavioralFrequencyFilters,
  };
}

export function setEntryVisibility(props: EntryRowProps, show: boolean) {
  props.updateEntry(Action.Edit, {
    ...props.entry,
    isHidden: !show,
  });

  props.dispatchTrack(QueryEntrySectionTrackingEvent.ToggleVisibility, {
    ...props.getEntryTrackingProps(),
    'is hidden': !show,
  });
}

export function openCustomBucket(props: EntryRowProps, entryPoint: ControlSelectEntryPoint) {
  const entry = props.entry as QueryBucketableEntry;
  const customBucket = entry?.customBucket;
  const type = getEntryPropertyType(entry);

  props.setActivePart(EntryPart.CustomBucketing, `open`);

  props.dispatchTrack(QueryEntrySectionTrackingEvent.OpenCustomBucketPane, {
    ...props.getEntryTrackingProps(),
    ...(customBucket
      ? {
          'Bucket min': customBucket.min,
          'Bucket max': customBucket.max,
          'Bucket size': customBucket.bucketSize,
        }
      : {}),
    'Is editing': !!customBucket,
    entryPoint,
    'Property Type': type ? (type.isList ? PropertyType.List : type.type) : null,
  });
}

export async function mapLookupTable(props: EntryRowProps, segmentation: Segmentation) {
  const property = segmentationToPropertyItem(segmentation);

  const attrs: Omit<MapLookupTableModalAttrs, `open`> = {
    'project-id': props.sectionProps.projectId,
    'workspace-id': props.sectionProps.workspaceId,
    'user-id': props.sectionProps.userId,
    'project-has-people': props.sectionProps.projectHasPeople,
    'selected-property': JSON.stringify(property) as unknown as PropertyItemWithValue,
    'event-names': props.sectionProps.eventNames,
    'disable-property-selection': true,
    elref: `mapLookupTableModal`,
    type: `temporary-map`,
  };

  props.dispatchTrack(QueryEntrySectionTrackingEvent.MapTableClicked, {
    ...props.getEntryTrackingProps(props.entry),
  });

  await promisifyModal({
    tagName: `mp-map-lookup-table-modal`,
    closeEvents: [`close`],
    attrs,
  });
}

export function canMapPropToLookupTable(prop: Property): boolean {
  if (Array.isArray(prop) || Array.isArray(prop.value) || prop.behavior) {
    return false;
  }
  const custom = Boolean(prop.customPropertyId);
  const value = custom ? prop.customPropertyId : prop.value;
  if (getTempLookupTableMappings(prop.resourceType, custom).has(value)) {
    return false;
  }

  return !propIneligibleForMappingReason(prop, true);
}

export function canUnmapPropFromLookupTable(prop: Property) {
  if (!prop.resourceType || !prop.value) {
    return false;
  }

  const custom = Boolean(prop.customPropertyId);
  const name = Array.isArray(prop.value) ? prop.value[0] : prop.value;
  const value = custom ? prop.customPropertyId : name;
  return getTempLookupTableMappings(prop.resourceType, custom).has(value);
}

/**
 * Prompts for a custom label on a given entry.
 */
// todo: this should return the new label and all row-specific response handling / updating should be in the rows
export async function promptRename(props: EntryRowProps, i18n: LocalizationStore) {
  let entry = props.entry;
  const {print} = i18n;

  const getEntryLabel = (etr: Entry) => {
    if (isMetricEntry(etr)) {
      return etr.behavior.name;
    } else if (isSavedMetricEntry(etr)) {
      return getSavedMetricName(etr, props.metricsCtx);
    } else if (isFormulaEntry(etr)) {
      return getFormulaEntryName(etr, props.metricsCtx, props.behaviorsCtx);
    } else if (isFilterEntry(etr)) {
      return new PropertyFilter(etr.event).getValuesLabel(false);
    }

    return print(`label` in etr.event ? etr.event.label : ``);
  };

  const isCustom = `event` in entry && `custom` in entry.event && !!entry.event.custom;
  const label = getEntryLabel(entry);

  const inputValidator = (stagedInput: string) => {
    const trimmed = stagedInput.trim();
    const labelIsUnchanged = trimmed === label;
    const entries = props.getEntries();
    const isValid =
      labelIsUnchanged ||
      entries.every((etr) => {
        const entryLabel = getEntryLabel(etr);
        const renamedLabel = `event` in etr && `renamed` in etr.event && etr.event.renamed;

        return trimmed !== entryLabel && trimmed !== renamedLabel;
      });

    return isValid ? null : `Name is already used.`;
  };

  let initialInputValue;
  if (isMetricEntry(entry)) {
    const isSavedBehavior = !!entry.behavior.id;
    initialInputValue = isSavedBehavior ? label : entry.behavior.renamed || label || ``;
  } else if (isFormulaEntry(entry) || isSavedMetricEntry(entry)) {
    initialInputValue = label;
  } else {
    initialInputValue = (entry as QueryEventEntry).event.renamed || label;
  }

  const promptResponse = await prompt(
    {
      'initial-input-value': initialInputValue,
      'allow-empty-input-value': !isMetricEntry(entry) && !isFormulaEntry(entry),
      'show-input': true,
    },
    {inputValidator},
    {
      title: ({t}) => t(`query-builder.actions.rename`),
      confirmButtonLabel: ({t}) => t(`common.done`),
    },
  );

  if (promptResponse.didConfirm) {
    const newLabel = promptResponse.inputValue;
    const hasLabel = label !== newLabel && newLabel !== ``;
    if (isMetricEntry(entry)) {
      const isSavedBehavior = !!entry.behavior.id;
      if (isSavedBehavior) {
        entry = {
          ...entry,
          behavior: {
            ...entry.behavior,
            ...{name: newLabel.trim() ? newLabel : null},
          },
          hasUnsavedChanges: newLabel !== label,
        } as MetricEntry;
      } else {
        entry = {
          ...entry,
          behavior: {
            ...omit(entry.behavior, [`renamed`]),
            ...{renamed: newLabel.trim() ? newLabel : null},
          },
        } as MetricEntry;
      }
      const trackEventsMapper = {
        [MetricType.Funnel]: QueryEntrySectionTrackingEvent.RenameFunnelMetric,
        [MetricType.Retention]: QueryEntrySectionTrackingEvent.RenameRetentionMetric,
        [MetricType.RetentionFrequency]: QueryEntrySectionTrackingEvent.RenameRetentionFrequencyMetric,
      };
      const trackEvent = trackEventsMapper[entry.behavior.type];
      props.dispatchTrack(trackEvent, {
        ...props.getEntryTrackingProps(entry),
        'Renamed name': newLabel,
      });
      props.updateEntry(Action.Edit, entry);
    } else if (isSavedMetricEntry(entry)) {
      props.updateEntry(Action.Edit, {...entry, event: {...entry.event, name: newLabel}, isExpanded: true});
    } else if (isFormulaEntry(entry)) {
      props.dispatchTrack(QueryEntrySectionTrackingEvent.RenameFormula, {
        ...props.getEntryTrackingProps(entry),
        'Renamed name': newLabel,
      });
      const newEntry = getUpdatedFormulaEntry(entry, {name: newLabel}, props.metricsCtx, props.behaviorsCtx);
      props.updateEntry(Action.Edit, newEntry);
    } else {
      entry = {
        ...entry,
        event: {
          ...omit(entry.event, [`renamed`]),
          ...{renamed: hasLabel ? newLabel : null},
        },
      } as QueryFilterEntry | QueryEventEntry;
      props.dispatchTrack(QueryEntrySectionTrackingEvent.RenameEvent, {
        ...props.getEntryTrackingProps(entry),
        'is custom event': isCustom,
        'is customized group': isFilterEntry(entry),
        'Renamed name': newLabel,
        'is removing rename': !hasLabel,
      });
      props.updateEntry(Action.Edit, entry);
    }
  }
}
